import { isFragmentRef, safeHref, safeImageSrc, safeImageSrcset } from './safeUrl'

// The viewer's only HTML sanitiser. Every piece of shared markup -- an office
// document's body, a desk doc or slide, a captured widget -- goes through this
// before it reaches the page.
//
// It is an allowlist, not a denylist. The markup was produced by the desktop's
// own renderers (Tiptap's generateHTML, the office snapshot builders, the
// widget capture), but it arrives over a public URL and the server stores it
// without inspecting it, so the viewer must assume a hostile author. Elements
// and attributes it does not know are removed; it never tries to enumerate the
// bad ones. The vocabulary below is the union of what those producers emit.
//
// Three properties the rest of the viewer relies on:
//
// 1. Parsing happens in a <template>'s content, which is inert: nothing in the
//    input loads, runs or fires an event while it is being examined. Not
//    DOMParser: in Chromium a DOMParser document inherits the page's CSP, so
//    merely parsing a hostile <style> or <base> -- before the sanitiser has
//    even seen it -- files Content-Security-Policy violation reports. Template
//    content is checked by nobody until it is inserted, and this never inserts
//    it; it inserts the sanitised copies.
// 2. The result is returned as DOM nodes, not as a string. Serialising and
//    re-parsing is where mutation XSS lives (markup that is harmless in one
//    parse and executable in the next -- noscript, math, foreign content), so
//    the nodes that were checked are the nodes that are inserted.
// 3. Every URL-bearing attribute goes through the same rules as a link widget
//    (src/safeUrl.ts), whatever element it is on, before the allowlist is even
//    consulted -- so widening the allowlist later cannot let a URL through
//    unchecked.

const SVG_NS = 'http://www.w3.org/2000/svg'
const HTML_NS = 'http://www.w3.org/1999/xhtml'

/** HTML elements kept as they are (attributes still filtered). */
const HTML_ELEMENTS: ReadonlySet<string> = new Set([
  'a', 'abbr', 'address', 'article', 'aside', 'b', 'bdi', 'bdo', 'blockquote', 'br',
  'caption', 'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'details', 'dfn', 'div',
  'dl', 'dt', 'em', 'figcaption', 'figure', 'footer', 'h1', 'h2', 'h3', 'h4', 'h5',
  'h6', 'header', 'hr', 'i', 'img', 'input', 'ins', 'kbd', 'label', 'li', 'main',
  'mark', 'nav', 'ol', 'p', 'pre', 'q', 's', 'samp', 'section', 'small', 'span',
  'strike', 'strong', 'sub', 'summary', 'sup', 'table', 'tbody', 'td', 'tfoot', 'th',
  'thead', 'time', 'tr', 'u', 'ul', 'var', 'wbr'
])

/**
 * SVG drawing elements, matching the desktop capture's allowlist. Not here, on
 * purpose: foreignObject (HTML through the side door), script, a, image,
 * feImage, and the animation elements, which can rewrite another element's
 * href to a script URL after the fact.
 */
const SVG_ELEMENTS: ReadonlySet<string> = new Set([
  'svg', 'g', 'path', 'rect', 'circle', 'ellipse', 'line', 'polyline', 'polygon',
  'text', 'tspan', 'defs', 'use', 'symbol', 'title', 'desc', 'linearGradient',
  'radialGradient', 'stop'
])

/**
 * Removed together with everything inside them. Their content is either code,
 * a document of its own, or text that is only text because of how a different
 * parser would read it.
 */
const DROP_HTML: ReadonlySet<string> = new Set([
  'script', 'style', 'noscript', 'template', 'iframe', 'frame', 'frameset', 'object',
  'embed', 'applet', 'param', 'link', 'meta', 'base', 'title', 'head', 'textarea',
  'select', 'option', 'optgroup', 'datalist', 'canvas', 'video', 'audio', 'source',
  'track', 'map', 'area', 'portal', 'xmp', 'plaintext', 'listing', 'noembed',
  'noframes', 'dialog'
])
const DROP_SVG: ReadonlySet<string> = new Set([
  'script', 'style', 'foreignObject', 'image', 'feImage', 'animate', 'animateMotion',
  'animateTransform', 'set', 'discard', 'handler', 'listener', 'iframe', 'video',
  'audio', 'canvas'
])

type UrlRule = 'link' | 'image' | 'imageset' | 'never'

/**
 * Every attribute that can carry a URL (or a document, for srcdoc), and the
 * rule it is held to. Applied on every element before the allowlist, so the
 * same string gets the same verdict whether it sits in an href, a formaction or
 * a poster. 'never' means no shared content may set it at all.
 */
const URL_ATTRIBUTES: Readonly<Record<string, UrlRule>> = {
  href: 'link',
  'xlink:href': 'link',
  action: 'link',
  formaction: 'link',
  cite: 'link',
  longdesc: 'link',
  src: 'image',
  poster: 'image',
  background: 'image',
  lowsrc: 'image',
  dynsrc: 'image',
  srcset: 'imageset',
  imagesrcset: 'imageset',
  ping: 'never',
  data: 'never',
  codebase: 'never',
  classid: 'never',
  archive: 'never',
  manifest: 'never',
  profile: 'never',
  usemap: 'never',
  icon: 'never',
  srcdoc: 'never',
  'xml:base': 'never',
  'xlink:base': 'never'
}

/** Attributes any kept element may carry (after URL and style checks). */
const GLOBAL_ATTRIBUTES: ReadonlySet<string> = new Set(['class', 'style', 'title', 'dir', 'lang', 'role'])

/** Per-element extras for HTML. */
const HTML_ATTRIBUTES: Readonly<Record<string, ReadonlySet<string>>> = {
  a: new Set(['href']),
  img: new Set(['src', 'alt', 'width', 'height']),
  td: new Set(['colspan', 'rowspan', 'align', 'valign']),
  th: new Set(['colspan', 'rowspan', 'align', 'valign', 'scope']),
  col: new Set(['span', 'width']),
  colgroup: new Set(['span', 'width']),
  ol: new Set(['start', 'type', 'reversed']),
  li: new Set(['value']),
  input: new Set(['type', 'checked']),
  details: new Set(['open']),
  time: new Set(['datetime'])
}

/** Presentation attributes an SVG element needs to draw. None can execute. */
const SVG_ATTRIBUTES: ReadonlySet<string> = new Set([
  'd', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin',
  'stroke-dasharray', 'stroke-dashoffset', 'stroke-miterlimit', 'stroke-opacity',
  'fill-opacity', 'fill-rule', 'clip-rule', 'opacity', 'viewBox', 'preserveAspectRatio',
  'width', 'height', 'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'fx', 'fy', 'dx', 'dy', 'points', 'transform', 'offset', 'stop-color', 'stop-opacity',
  'gradientUnits', 'gradientTransform', 'text-anchor', 'dominant-baseline',
  'font-size', 'font-family', 'font-weight', 'xmlns'
])

/**
 * CSS functions that fetch or compute something other than through a url()
 * we can check, or that only exist to run code (expression, -moz-binding).
 */
const CSS_FORBIDDEN = /expression\s*\(|-moz-binding|behavior\s*:|javascript:|vbscript:|image-set\s*\(|(?:^|[^-\w])image\s*\(|cross-fade\s*\(|element\s*\(|(?:^|[^-\w])src\s*\(|attr\s*\(|@import/i

/** A url() token as CSSOM serialises it: url("..."), always double-quoted. */
const CSS_URL = /url\(\s*(?:"((?:[^"\\]|\\.)*)"|'((?:[^'\\]|\\.)*)'|([^\s"'()\\]*))\s*\)/gi

/**
 * One inline-style value is safe when it fetches nothing it should not: no
 * escapes we would have to second-guess, no forbidden functions, and every
 * url() an image URL that safeImageSrc accepts.
 */
function isSafeCssValue(value: string): boolean {
  if (value.includes('\\')) return false
  if (CSS_FORBIDDEN.test(value)) return false
  const opened = (value.match(/url\s*\(/gi) ?? []).length
  let checked = 0
  for (const m of value.matchAll(CSS_URL)) {
    checked++
    const target = m[1] ?? m[2] ?? m[3] ?? ''
    if (!safeImageSrc(target)) return false
  }
  // A url( the pattern could not read is a url( we did not check.
  return opened === checked
}

/**
 * Re-examine an inline style through the browser's own CSS parser: each
 * declaration it parsed (custom properties included) is checked on its own,
 * and the attribute is then REWRITTEN from what survived. Rewriting matters:
 * a declaration this engine could not parse is invisible to the CSSOM but
 * still sitting in the attribute text, where another parse might read it.
 */
function sanitizeStyle(el: Element): void {
  const style = (el as HTMLElement).style as CSSStyleDeclaration | undefined
  if (!style) {
    el.removeAttribute('style')
    return
  }
  for (let i = style.length - 1; i >= 0; i--) {
    const prop = style.item(i)
    if (!isSafeCssValue(style.getPropertyValue(prop))) style.removeProperty(prop)
  }
  const text = style.length === 0 ? '' : style.cssText
  if (text === '') el.removeAttribute('style')
  else el.setAttribute('style', text)
}

type Verdict = 'keep' | 'unwrap' | 'drop'

function verdictFor(el: Element): Verdict {
  const name = el.localName
  if (el.namespaceURI === HTML_NS) {
    if (DROP_HTML.has(name)) return 'drop'
    return HTML_ELEMENTS.has(name) ? 'keep' : 'unwrap'
  }
  if (el.namespaceURI === SVG_NS) {
    if (DROP_SVG.has(name)) return 'drop'
    return SVG_ELEMENTS.has(name) ? 'keep' : 'unwrap'
  }
  // MathML and any other namespace: a different parser's rules (and the home
  // of most mutation-XSS tricks), and nothing a desk or document emits.
  return 'drop'
}

function isAllowedAttribute(el: Element, name: string): boolean {
  if (GLOBAL_ATTRIBUTES.has(name)) return true
  if (/^aria-[a-z]+$/.test(name)) return true
  // data-* is inert unless a script reads it; the viewer's do not. Tiptap marks
  // task lists with data-type / data-checked.
  if (/^data-[a-z0-9_.-]+$/.test(name)) return true
  if (el.namespaceURI === SVG_NS) {
    if (el.localName === 'use' && (name === 'href' || name === 'xlink:href')) return true
    return SVG_ATTRIBUTES.has(name)
  }
  return HTML_ATTRIBUTES[el.localName]?.has(name) ?? false
}

function sanitizeAttributes(el: Element): void {
  const isUse = el.namespaceURI === SVG_NS && el.localName === 'use'
  for (const attr of Array.from(el.attributes)) {
    const name = el.namespaceURI === SVG_NS ? attr.name : attr.name.toLowerCase()
    const key = name.toLowerCase()
    // Event handlers: never, on anything.
    if (key.startsWith('on')) {
      el.removeAttribute(attr.name)
      continue
    }
    const rule = URL_ATTRIBUTES[key]
    if (rule) {
      let safe: string | null = null
      if (isUse && (key === 'href' || key === 'xlink:href')) {
        // A <use> may only reference a fragment already in the markup.
        safe = isFragmentRef(attr.value) ? attr.value : null
      } else if (rule === 'link') safe = safeHref(attr.value)
      else if (rule === 'image') safe = safeImageSrc(attr.value)
      else if (rule === 'imageset') safe = safeImageSrcset(attr.value)
      if (safe === null) {
        el.removeAttribute(attr.name)
        continue
      }
      // Keep the parser's serialisation: the checked value is the used value.
      attr.value = safe
    }
    if (!isAllowedAttribute(el, name)) {
      el.removeAttribute(attr.name)
      continue
    }
    if (key === 'style') sanitizeStyle(el)
  }
}

/** Element-specific fix-ups once attributes have been filtered. */
function finishElement(el: Element): 'keep' | 'unwrap' | 'drop' {
  if (el.namespaceURI === SVG_NS) {
    if (el.localName === 'use' && !el.hasAttribute('href') && !el.hasAttribute('xlink:href')) return 'drop'
    return 'keep'
  }
  switch (el.localName) {
    case 'a':
      // A link with no safe destination is just its text.
      if (!el.hasAttribute('href')) return 'unwrap'
      // Shared links open away from the viewer and cannot reach back into it.
      el.setAttribute('target', '_blank')
      el.setAttribute('rel', 'noopener noreferrer nofollow')
      return 'keep'
    case 'img':
      if (!el.hasAttribute('src')) return 'drop'
      // The page URL carries the share token; an author's image host has no
      // business seeing it.
      el.setAttribute('referrerpolicy', 'no-referrer')
      el.setAttribute('loading', 'lazy')
      return 'keep'
    case 'input':
      // Only a task list's tick box survives, and it cannot be ticked.
      if ((el.getAttribute('type') ?? '').toLowerCase() !== 'checkbox') return 'drop'
      el.setAttribute('disabled', '')
      return 'keep'
    default:
      return 'keep'
  }
}

function sanitizeChildren(parent: Node): void {
  for (const child of Array.from(parent.childNodes)) sanitizeNode(child)
}

function sanitizeNode(node: Node): void {
  if (node.nodeType === 3 /* TEXT_NODE */) return
  if (node.nodeType !== 1 /* ELEMENT_NODE */) {
    // Comments, processing instructions, CDATA: nothing a reader needs.
    node.parentNode?.removeChild(node)
    return
  }
  const el = node as Element
  const verdict = verdictFor(el)
  if (verdict === 'drop') {
    el.remove()
    return
  }
  // Children first, so an unwrapped element's promoted children are already
  // clean when they move up.
  sanitizeChildren(el)
  if (verdict === 'unwrap') {
    el.replaceWith(...Array.from(el.childNodes))
    return
  }
  sanitizeAttributes(el)
  const after = finishElement(el)
  if (after === 'drop') el.remove()
  else if (after === 'unwrap') el.replaceWith(...Array.from(el.childNodes))
}

/**
 * Sanitise untrusted markup into nodes owned by `target`, ready to append.
 * Never throws: input that cannot be parsed or walked yields an empty fragment
 * (fail closed), never the original markup.
 */
export function sanitizeHtml(html: unknown, target: Document = document): DocumentFragment {
  const out = target.createDocumentFragment()
  if (typeof html !== 'string' || html === '') return out
  try {
    // Fragment parsing in a template context: <html>, <head> and <body> tags
    // are ignored rather than applied (so a body onload has nowhere to go),
    // and a stray </template> cannot end the context early.
    const inert = target.createElement('template')
    inert.innerHTML = html
    sanitizeChildren(inert.content)
    for (const child of Array.from(inert.content.childNodes)) out.appendChild(target.importNode(child, true))
    return out
  } catch {
    return target.createDocumentFragment()
  }
}

/** The sanitised markup as a string. For tests and diagnostics only. */
export function sanitizeHtmlToString(html: unknown, target: Document = document): string {
  const box = target.createElement('div')
  box.appendChild(sanitizeHtml(html, target))
  return box.innerHTML
}
